import {batch} from 'react-redux';
import pdfMake from 'pdfmake/build/pdfmake';
import vfsFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';

import {selectIsHumidifierInstalled} from 'stores/selectors/humidifierSelectors';
import {resolveIntl} from './commonBusiness';
import {
  elaborateLastDispenseReport,
  getLastDispenseReport,
  updateDispenseReport,
} from './dispenseReportExplorerBusiness';
import {
  setReportProtocolName,
  setReportSummary,
  setReportSummaryCsv,
  setReportCreatedTime,
  setReportId,
  setPreviousReport,
  setReportProtocolRunBy,
} from '../actions/reportStateAction';
import {dispenseReportHeader, PageSizes, UserRoleLevel, ViewMode} from '../../constants/dataEnum';
import {isRoleLevel} from './userManagementBusiness';
import {addTemporaryState} from '../actions/temporaryStateAction';
import {DISPENSE_REPORT_NOTIFICATION} from '../../constants/temporaryStateType';
import {isProtocolExecutor} from './stateBusiness';

const dateFormat = 'YYYY-MM-DD HH:mm:ss';

const intl = resolveIntl();

const generatePdfBuffer = (pageSize) => (dispatch, getState) => {
  const {reportState, humidifierState} = getState();
  const {summary, protocolName, protocolRunBy} = reportState;
  const {isHumidifierInstalled} = humidifierState;

  const formattedData = [];

  summary.forEach((data) => {
    const styleName = 'tableRowSucceeded';
    const styleNameLeft = 'tableRowSucceededLeft';

    formattedData.push([
      {text: data.destinationPlateBarcode, style: styleName},
      {text: data.destinationPlateName, style: styleName},
      {text: data.destinationWell, style: styleName},
      {text: data.transferVolume, style: styleName},
      {text: data.sourcePlateBarcode, style: styleName},
      {text: data.sourcePlateName, style: styleName},
      {text: data.sourceWell, style: styleName},
      {text: data.number, style: styleName},
      {text: data.tips, style: styleNameLeft},
      {text: data.status, style: styleName},
      {text: data.errorCode, style: styleName},
      {text: data.start, style: styleName},
      {text: data.end, style: styleName},
      ...(isHumidifierInstalled
        ? [
            {text: data.humidity, style: styleName},
            {text: data.temperature, style: styleName},
            {text: data.isHumidifierDoorOpened, style: styleName},
          ]
        : []),
      {text: data.notes, style: styleName},
    ]);
  });

  const {vfs} = vfsFonts.pdfMake;
  pdfMake.vfs = vfs;

  const docDefinition = {
    content: [
      {
        text: intl.formatMessage({id: 'dispenseReport.title'}),
        style: {
          fontSize: 9,
          bold: true,
          alignment: 'center',
        },
      },
      {
        style: {
          fontSize: 9,
        },
        table: {
          headerRows: 1,
          widths: ['10%', '2%', '20%'],
          body: [
            [
              {
                text: intl.formatMessage({id: 'dispenseReport.label.protocolName'}),
                style: 'tableLabel',
              },
              {
                text: ':',
              },
              {
                text: protocolName,
              },
            ],
            [
              {
                text: intl.formatMessage({id: 'dispenseReport.label.protocolRunBy'}),
                style: 'tableLabel',
              },
              {
                text: ':',
                style: 'tableValue',
              },
              {
                text: protocolRunBy,
                style: 'tableValue',
              },
            ],
          ],
        },
        layout: 'noBorders',
      },
      {
        style: {
          fontSize: 9,
        },
        table: {
          headerRows: 1,
          widths: isHumidifierInstalled
            ? ['8%', '8%', '6%', '6%', '6%', '6%', '6%', '3%', '6%', '6%', '6%', '6%', '6%', '6%', '6%', '6%', '8%']
            : ['8%', '8%', '6%', '6%', '8%', '8%', '6%', '3%', '6%', '6%', '8%', '8%', '8%', '11%'],
          body: [
            [
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.firstColumn'}),
                style: 'tableHeader',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.secondColumn'}),
                style: 'tableHeader',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.thirdColumn'}),
                style: 'tableHeader',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.fourthColumn'}),
                style: 'tableHeader',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.fivethColumn'}),
                style: 'tableHeader',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.sixthColumn'}),
                style: 'tableHeader',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.seventhColumn'}),
                style: 'tableHeaderLeft',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.eighthColumn'}),
                style: 'tableHeaderLeft',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.ninthColumn'}),
                style: 'tableHeaderLeft',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.tenthColumn'}),
                style: 'tableHeaderLeft',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.eleventhColumn'}),
                style: 'tableHeaderLeft',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.twelfthColumn'}),
                style: 'tableHeaderLeft',
              },
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.thirteenthColumn'}),
                style: 'tableHeaderLeft',
              },
              ...(isHumidifierInstalled
                ? [
                    {
                      text: intl.formatMessage({id: 'dispenseReport.tableHeader.fourteenthColumn'}),
                      style: 'tableHeaderLeft',
                    },
                    {
                      text: intl.formatMessage({id: 'dispenseReport.tableHeader.fifteenthColumn'}),
                      style: 'tableHeaderLeft',
                    },
                    {
                      text: intl.formatMessage({id: 'dispenseReport.tableHeader.sixteenthColumn'}),
                      style: 'tableHeaderLeft',
                    },
                  ]
                : []),
              {
                text: intl.formatMessage({id: 'dispenseReport.tableHeader.seventeenthColumn'}),
                style: 'tableHeaderLeft',
              },
            ],
            ...formattedData,
          ],
        },
        layout: 'lightHorizontalLines',
      },
    ],
    styles: {
      tableLabel: {
        bold: true,
        alignment: 'left',
      },
      dispenseReportTable: {
        margin: [0, 5, 0, 15],
      },
      tableHeader: {
        bold: true,
        alignment: 'center',
        margin: [0, 5, 0, 5],
      },
      tableHeaderLeft: {
        bold: true,
        alignment: 'left',
        margin: [0, 5, 0, 5],
      },
      tableRowSucceeded: {
        alignment: 'center',
        color: 'black',
        margin: [0, 5, 0, 5],
      },
      tableRowSucceededLeft: {
        alignment: 'left',
        color: 'black',
        margin: [0, 5, 0, 5],
      },
      tableRowFailed: {
        alignment: 'center',
        color: 'red',
        margin: [0, 5, 0, 5],
      },
      tableRowFailedLeft: {
        alignment: 'left',
        color: 'red',
        margin: [0, 5, 0, 5],
      },
    },
    defaultStyle: {
      fontSize: 10,
    },
    pageSize,
    pageOrientation: 'landscape',
    pageMargins: [10, 30, 10, 30],
  };

  return pdfMake.createPdf(docDefinition);
};

export const dispenseRepostCsvToObject = (csv, excludedHeaderKeys = []) => {
  const lines = csv?.split('\n') ?? [];
  const result = [];
  const headers = excludedHeaderKeys.length
    ? dispenseReportHeader.filter((header) => !excludedHeaderKeys.includes(header))
    : dispenseReportHeader;

  const protocolRunnerData = lines.length ? lines[1]?.replace('\r', '')?.split(',') : [];
  const runBy = protocolRunnerData && (protocolRunnerData[1] ?? '');

  for (let i = 4; i < lines.length; i += 1) {
    const obj = {};
    const currentline = lines[i].replace('\r', '').split(',');
    if (currentline[0]?.length) {
      for (let j = 0; j < headers.length; j += 1) {
        obj[headers[j]] = currentline[j];
      }
      result.push(obj);
    }
  }

  return [runBy, result];
};

export const getReport =
  (
    // eslint-disable-next-line default-param-last
    callBack = () => {},
    isDownloaded,
  ) =>
  async (dispatch, getState) => {
    const {runtimeState} = getState();
    const {protocolName} = runtimeState;
    const isHumidifierInstalled = selectIsHumidifierInstalled(getState());
    const report = await dispatch(getLastDispenseReport(isDownloaded));
    console.log(isDownloaded);

    batch(() => {
      dispatch(setReportProtocolName(protocolName));

      if (report) {
        const excludedItems = isHumidifierInstalled ? [] : ['humidity', 'temperature', 'isHumidifierDoorOpened'];
        const [runBy, dispenseReportObject] = dispenseRepostCsvToObject(report.content, excludedItems);
        dispatch(setReportProtocolRunBy(runBy));
        dispatch(setReportSummary(dispenseReportObject));
        dispatch(setReportSummaryCsv(report.content));
        dispatch(setReportCreatedTime(report.createdTime));
        dispatch(setReportId(report.id));
      }

      callBack();
    });
  };

export const downloadDispenseReport = (pageSize) => (dispatch, getState) => {
  const {reportState} = getState();
  const {protocolName, createdTime, protocolRunBy} = reportState;

  const pdfBuff = dispatch(generatePdfBuffer(pageSize));
  const timeStamp = moment.utc(createdTime).local().format(dateFormat);

  pdfBuff.download(`Dispense Report${protocolRunBy ? `_${protocolRunBy}` : ''}_${protocolName}_${timeStamp}.pdf`);
};

export const printDispenseReport = () => (dispatch) => {
  const pdfBuff = dispatch(generatePdfBuffer(PageSizes[0].value));

  return pdfBuff;
};

const isDuplicateDownload = (fromProtocolExecutionFinished, reportToDownload) => async (dispatch, getState) => {
  const {reportState} = getState();
  const {previousReport} = reportState;

  if (!previousReport?.id) {
    await dispatch(setPreviousReport(reportToDownload));
    return false;
  }

  const {id: prevId, downloadedTime: prevDownloadedTime} = previousReport;

  if (fromProtocolExecutionFinished) {
    const prevDLTime = moment(prevDownloadedTime);
    const currentReportDLTime = moment(reportToDownload.downloadedTime);
    const timeDifference = prevDLTime.diff(currentReportDLTime, 'seconds');

    if (prevId === reportToDownload.id && timeDifference < 30) {
      return true;
    }
  }

  return false;
};

const createReportDownloadFile = (data, fileName, extension) => {
  const newFileName = `${fileName}${extension}`;

  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', newFileName);
  document.body.appendChild(link);
  link.click();
};

export const downloadDispenseReportCsv =
  (
    // eslint-disable-next-line default-param-last
    fromProtocolExecutionFinished = false,
    report,
    fileName,
  ) =>
  async (dispatch, getState) => {
    const state = getState();
    const {runtimeState, reportState, sessionState} = state;
    const {summaryCsv, createdTime, protocolRunBy} = reportState;

    let blob = new Blob([summaryCsv], {type: 'text/csv;charset=utf-8;'});
    const timeStamp = moment.utc(createdTime).local().format(dateFormat);
    const downloadedTime = moment.utc(Date.now()).local().format(dateFormat);
    let userId = sessionState?.user?.userId;

    let filename = runtimeState?.protocolName
      ? `Dispense Report${protocolRunBy ? `_${protocolRunBy}` : ''}_${runtimeState.protocolName}_${timeStamp}`
      : `Dispense Report${protocolRunBy ? `_${protocolRunBy}` : ''}_${timeStamp}`;

    let reportId = reportState.id;

    if (report) {
      blob = report.content;
      reportId = report.id;
      filename = fileName;
      userId = report.user.userId;
    }

    console.log('!! requesting to download dispense report for report ', reportId);
    const currentReports = {id: reportId, downloadedTime};
    const isDownloadDuplicate = await dispatch(isDuplicateDownload(fromProtocolExecutionFinished, currentReports));

    if (!isDownloadDuplicate && reportId) {
      await dispatch(setPreviousReport(currentReports));
      await createReportDownloadFile(blob, filename, '.csv');
      await dispatch(updateDispenseReport(reportId, {IsDownloaded: true}, userId));
    }
  };

export const checkDownloadReportPermission = (isExecutor, isTessaraProtocol) => (dispatch, getState) => {
  const {reportState} = getState();
  const isTessaraSuperUser = dispatch(isRoleLevel(UserRoleLevel.TESSARA_SUPER_USER));

  const isTSRSuperUserRunningTSRProtocol = isTessaraProtocol && isTessaraSuperUser;

  return isExecutor && !reportState.isDownloading && isTessaraProtocol ? isTSRSuperUserRunningTSRProtocol : true;
};

export const fetchAndDownloadLatestDispenseReport =
  (isCheckPermission, isTessaraProtocol) => async (dispatch, getState) => {
    const {appState} = getState();
    const {viewMode} = appState;

    const isExecutor = await dispatch(isProtocolExecutor());
    const isDownloadReportAllowed = dispatch(checkDownloadReportPermission(isExecutor, isTessaraProtocol));

    if (isCheckPermission && !isDownloadReportAllowed) return;

    await dispatch(elaborateLastDispenseReport(() => {}, false));
    await dispatch(getReport(() => {}, false));
    dispatch(downloadDispenseReportCsv(true));

    if (
      viewMode !== ViewMode.SETUP_WIZARD &&
      viewMode !== ViewMode.LOGIN &&
      viewMode !== ViewMode.LOGIN_CONFIRMATION &&
      viewMode !== ViewMode.RUNTIME &&
      isExecutor
    ) {
      dispatch(addTemporaryState(DISPENSE_REPORT_NOTIFICATION, {show: true}));
    }
  };
